import React from 'react';
import { 
  AlertOctagon, AlertTriangle, Clock, CalendarClock, 
  User, RefreshCw, Shield, Link, FileQuestion
} from 'lucide-react';

/**
 * TrackingInsightsBar - Quick toggle filters for tracking pages
 * Provides context-specific quick filters for Issues, Dependencies, and Decisions
 */
const TrackingInsightsBar = ({
  trackingType = 'issues', // 'issues' | 'dependencies' | 'decisions'
  filters = {},
  onFilterChange,
  counts = {},
  currentUser = null,
  className = '',
}) => {
  // Get chips configuration based on tracking type
  const getChips = () => {
    switch (trackingType) {
      case 'issues':
        return [
          { 
            id: 'blocked_only', 
            label: 'Blocked', 
            icon: AlertOctagon,
            color: '#8E44AD', 
            count: counts.blocked 
          },
          { 
            id: 'high_severity_only', 
            label: 'High Severity', 
            icon: AlertTriangle,
            color: '#E74C3C', 
            count: counts.high_severity 
          },
          { 
            id: 'overdue_only', 
            label: 'Overdue', 
            icon: Clock,
            color: '#C62828', 
            count: counts.overdue 
          },
          { 
            id: 'due_this_week', 
            label: 'Due This Week', 
            icon: CalendarClock,
            count: counts.due_this_week 
          },
          { 
            id: 'recently_updated', 
            label: 'Recently Updated', 
            icon: RefreshCw,
            count: counts.recently_updated 
          },
        ];
      
      case 'dependencies':
        return [
          { 
            id: 'blocking_only', 
            label: 'Blocking', 
            icon: AlertOctagon,
            color: '#E74C3C', 
            count: counts.blocking 
          },
          { 
            id: 'critical_only', 
            label: 'Critical', 
            icon: AlertTriangle,
            color: '#C62828', 
            count: counts.critical 
          },
          { 
            id: 'overdue_only', 
            label: 'Overdue', 
            icon: Clock,
            color: '#F57C00', 
            count: counts.overdue 
          },
          { 
            id: 'due_this_week', 
            label: 'Due This Week', 
            icon: CalendarClock,
            count: counts.due_this_week 
          },
          { 
            id: 'aging_over_30', 
            label: 'Aging >30 Days', 
            icon: Clock,
            color: '#8E44AD', 
            count: counts.aging_over_30 
          },
        ];
      
      case 'decisions':
        return [
          { 
            id: 'pending_only', 
            label: 'Pending', 
            icon: FileQuestion,
            color: '#F39C12', 
            count: counts.pending 
          },
          { 
            id: 'high_impact_only', 
            label: 'High Impact', 
            icon: AlertTriangle,
            color: '#E74C3C', 
            count: counts.high_impact 
          },
          { 
            id: 'steerco_only', 
            label: 'SteerCo Review', 
            icon: Shield,
            color: '#8E44AD', 
            count: counts.steerco 
          },
          { 
            id: 'overdue_only', 
            label: 'Overdue', 
            icon: Clock,
            color: '#C62828', 
            count: counts.overdue 
          },
          { 
            id: 'due_this_week', 
            label: 'Due This Week', 
            icon: CalendarClock,
            count: counts.due_this_week 
          },
        ];
      
      default:
        return [];
    }
  };

  // Add "My Items" if user is logged in
  const getChipsWithMyItems = () => {
    const chips = getChips();
    if (currentUser) {
      chips.push({
        id: 'my_items_only',
        label: 'My Items',
        icon: User,
        count: counts.myItems
      });
    }
    return chips;
  };

  const chips = getChipsWithMyItems();

  const activeChips = Object.entries(filters)
    .filter(([_, value]) => value === true)
    .map(([key]) => key);

  const handleToggle = (chipId) => {
    const newFilters = { ...filters };
    newFilters[chipId] = !filters[chipId];
    onFilterChange(newFilters);
  };

  const clearAllQuickFilters = () => {
    const clearedFilters = {};
    chips.forEach(chip => {
      clearedFilters[chip.id] = false;
    });
    onFilterChange(clearedFilters);
  };

  const hasActiveFilters = activeChips.length > 0;

  return (
    <div className={`bg-white border border-gray-200 rounded-lg p-3 ${className}`} data-testid="tracking-insights-bar">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-xs font-medium text-[var(--clr-text-muted)] uppercase tracking-wide whitespace-nowrap">
            Quick Filters
          </span>
          <div className="flex flex-wrap gap-2">
            {chips.map(chip => {
              const isActive = activeChips.includes(chip.id);
              const Icon = chip.icon;
              
              return (
                <button
                  key={chip.id}
                  onClick={() => handleToggle(chip.id)}
                  data-testid={`quick-filter-${chip.id}`}
                  className={`
                    inline-flex items-center rounded-full font-medium transition-all
                    px-2.5 py-1 text-xs gap-1
                    ${isActive 
                      ? 'text-white ring-2 ring-opacity-30' 
                      : 'bg-[var(--clr-surface-subtle)] text-[var(--clr-text-secondary)] hover:bg-gray-200'
                    }
                  `}
                  style={isActive ? { 
                    backgroundColor: chip.color || 'var(--clr-brand-primary)',
                    ringColor: chip.color || 'var(--clr-brand-primary)'
                  } : undefined}
                >
                  {Icon && <Icon className="w-3 h-3" />}
                  <span>{chip.label}</span>
                  {chip.count !== undefined && chip.count > 0 && (
                    <span className={`
                      ml-1 px-1.5 rounded-full text-xs
                      ${isActive ? 'bg-white bg-opacity-20' : 'bg-gray-300 bg-opacity-50'}
                    `}>
                      {chip.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
        
        {hasActiveFilters && (
          <button
            onClick={clearAllQuickFilters}
            className="text-xs text-[var(--clr-text-muted)] hover:text-[var(--clr-sev-blocked)] whitespace-nowrap"
            data-testid="clear-quick-filters"
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
};

export default TrackingInsightsBar;
